import { createRoute, useNavigate } from "@tanstack/react-router";
import { useSession } from "@/auth/client";
import { DealMetricsDetailPage } from "@/components/deal-metrics/deal-metrics-detail-page";
import { Route as authenticatedRoute } from "../_authenticated";

interface DetailSearchParams {
  metric: string;
  hubspotOwnerId?: string;
  startDate: string;
  endDate: string;
  prodPipelineId: string;
  salesPipelineId?: string;
  excludeOppStageIds?: string;
  excludeClosedStageIds?: string;
}

export const Route = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/deal-metrics/detail",
  validateSearch: (search: Record<string, unknown>): DetailSearchParams => ({
    metric: (search.metric as string) ?? "",
    hubspotOwnerId: search.hubspotOwnerId as string | undefined,
    startDate: (search.startDate as string) ?? "",
    endDate: (search.endDate as string) ?? "",
    prodPipelineId: (search.prodPipelineId as string) ?? "",
    salesPipelineId: search.salesPipelineId as string | undefined,
    excludeOppStageIds: search.excludeOppStageIds as string | undefined,
    excludeClosedStageIds: search.excludeClosedStageIds as string | undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(deps)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const [detailRes, pipelinesRes, propsRes] = await Promise.all([
      fetch(`/api/deal-metrics/detail?${params}`),
      fetch("/api/hubspot/pipelines/deals"),
      fetch("/api/hubspot-properties?limit=500"),
    ]);
    if (!detailRes.ok) {
      throw new Error(`Deal metrics detail: ${detailRes.status}`);
    }
    if (!pipelinesRes.ok) {
      throw new Error(`Pipelines: ${pipelinesRes.status}`);
    }
    const detail = await detailRes.json();
    const pipelinesData = await pipelinesRes.json();
    const propsData = propsRes.ok ? await propsRes.json() : { data: [] };
    const propertyLabels: Record<string, string> = {};
    for (const p of propsData.data ?? []) {
      propertyLabels[p.name] = p.label;
    }
    return { detail, pipelines: pipelinesData.results ?? [], propertyLabels };
  },
  component: DealMetricsDetailGuard,
});

function DealMetricsDetailGuard() {
  const { data: session } = useSession();
  const navigate = useNavigate();

  if ((session?.user as any)?.role !== "admin") {
    navigate({ to: "/my-dashboard" });
    return null;
  }

  return <DealMetricsDetailPage />;
}
